/**
 * Bullet impacts, one distinct synthesis per surface from the CONTRACTS.md enum,
 * plus ricochet whines and debris.
 *
 * Each impact is: a contact transient (how hard the surface is), a material
 * resonance (what the surface is made of), and a debris/particle layer (what comes
 * off it). Ricochet probability and brightness are per-surface too.
 */
import {
  modal, applyExpDecay, applyAD, bandpass, lowpass, highpass, sineSweep,
  granular, normalise as normArr, addInto, onePoleLP,
} from '../synth/dsp.js';
import { noiseSlice } from '../synth/noise.js';
import { bq, gain, playData } from '../synth/nodes.js';
import { buildRicochet, buildDebris } from './Weapons.js';

function emit(ac, dest, t0, data, g, filters = [], rate = 1) {
  const gn = gain(ac, g);
  let head = gn;
  for (let i = filters.length - 1; i >= 0; i--) { filters[i].connect(head); head = filters[i]; }
  gn.connect(dest);
  playData(ac, data, head, Math.max(0, t0), { rate });
  return gn;
}

const j = (rng, a = 0.1) => 1 + (rng() * 2 - 1) * a;

/**
 * Per-surface voicing.
 *  thumpF/thumpTau  : the low contact thud
 *  modes            : material resonance (relative to `res`)
 *  noiseF/noiseQ/noiseTau : the particulate spray
 *  ricochet         : 0..1 probability
 *  debris           : 'concrete' | 'metal' | 'glass' | null
 */
export const SURFACES = {
  concrete: {
    level: 0.56, thumpF: 170, thumpF1: 70, thumpTau: 0.030, thumpLevel: 0.55,
    res: 900, modes: [[1, 1, 0.008], [1.9, 0.4, 0.005], [3.1, 0.2, 0.003]], resLevel: 0.35,
    noiseF: 1600, noiseQ: 0.8, noiseTau: 0.055, noiseLevel: 0.9, hp: 180,
    ricochet: 0.30, ricoBase: 1500, debris: 'concrete', debrisLevel: 0.30, debrisSeconds: 0.42, duration: 0.55,
  },
  metal: {
    level: 0.62, thumpF: 220, thumpF1: 95, thumpTau: 0.018, thumpLevel: 0.35,
    res: 1800, modes: [[1, 1, 0.17], [2.41, 0.6, 0.105], [3.87, 0.4, 0.065], [5.9, 0.25, 0.035], [0.61, 0.5, 0.26]], resLevel: 0.75,
    noiseF: 4200, noiseQ: 1.1, noiseTau: 0.020, noiseLevel: 0.7, hp: 400,
    ricochet: 0.65, ricoBase: 2100, debris: 'metal', debrisLevel: 0.18, debrisSeconds: 0.40, duration: 0.95,
  },
  wood: {
    level: 0.54, thumpF: 200, thumpF1: 85, thumpTau: 0.035, thumpLevel: 0.50,
    res: 380, modes: [[1, 1, 0.035], [2.05, 0.45, 0.022], [3.4, 0.22, 0.014], [0.63, 0.5, 0.055]], resLevel: 0.60,
    noiseF: 1100, noiseQ: 0.9, noiseTau: 0.045, noiseLevel: 0.65, hp: 140,
    ricochet: 0.06, ricoBase: 1200, debris: 'concrete', debrisLevel: 0.22, debrisSeconds: 0.32, duration: 0.5,
  },
  dirt: {
    level: 0.46, thumpF: 140, thumpF1: 55, thumpTau: 0.045, thumpLevel: 0.70,
    res: 260, modes: [[1, 1, 0.010], [1.8, 0.3, 0.006]], resLevel: 0.18,
    noiseF: 620, noiseQ: 0.6, noiseTau: 0.065, noiseLevel: 0.8, hp: 70,
    ricochet: 0.0, ricoBase: 900, debris: 'concrete', debrisLevel: 0.20, debrisSeconds: 0.22, duration: 0.5,
  },
  sand: {
    level: 0.38, thumpF: 120, thumpF1: 52, thumpTau: 0.030, thumpLevel: 0.42,
    res: 300, modes: [[1, 1, 0.006]], resLevel: 0.08,
    noiseF: 3200, noiseQ: 0.5, noiseTau: 0.075, noiseLevel: 1.0, hp: 300,
    ricochet: 0.0, ricoBase: 900, debris: 'concrete', debrisLevel: 0.28, debrisSeconds: 0.24, duration: 0.55,
  },
  glass: {
    level: 0.6, thumpF: 260, thumpF1: 120, thumpTau: 0.012, thumpLevel: 0.22,
    res: 3200, modes: [[1, 1, 0.045], [1.73, 0.7, 0.030], [2.9, 0.5, 0.020], [4.6, 0.3, 0.012]], resLevel: 0.85,
    noiseF: 6200, noiseQ: 1.2, noiseTau: 0.028, noiseLevel: 0.8, hp: 900,
    ricochet: 0.10, ricoBase: 3000, debris: 'glass', debrisLevel: 0.55, debrisSeconds: 0.8, duration: 0.9,
  },
  fabric: {
    level: 0.34, thumpF: 130, thumpF1: 60, thumpTau: 0.028, thumpLevel: 0.45,
    res: 240, modes: [[1, 1, 0.008]], resLevel: 0.10,
    noiseF: 900, noiseQ: 0.5, noiseTau: 0.040, noiseLevel: 0.7, hp: 120,
    ricochet: 0.0, ricoBase: 800, debris: null, debrisLevel: 0, duration: 0.35,
  },
  water: {
    level: 0.46, thumpF: 90, thumpF1: 210, thumpTau: 0.055, thumpLevel: 0.55, thumpUp: true,
    res: 700, modes: [[1, 1, 0.020], [1.5, 0.4, 0.014]], resLevel: 0.22,
    noiseF: 2600, noiseQ: 0.6, noiseTau: 0.10, noiseLevel: 0.85, hp: 200,
    ricochet: 0.0, ricoBase: 900, debris: null, debrisLevel: 0, duration: 0.65, splash: true,
  },
  flesh: {
    level: 0.52, thumpF: 150, thumpF1: 62, thumpTau: 0.038, thumpLevel: 0.80,
    res: 340, modes: [[1, 1, 0.012], [1.7, 0.35, 0.008]], resLevel: 0.22,
    noiseF: 700, noiseQ: 1.3, noiseTau: 0.048, noiseLevel: 0.75, hp: 90,
    ricochet: 0.0, ricoBase: 800, debris: null, debrisLevel: 0, duration: 0.45, wet: true,
  },
  foliage: {
    level: 0.32, thumpF: 200, thumpF1: 110, thumpTau: 0.010, thumpLevel: 0.15,
    res: 1400, modes: [[1, 1, 0.006]], resLevel: 0.10,
    noiseF: 3800, noiseQ: 0.5, noiseTau: 0.09, noiseLevel: 1.0, hp: 600,
    ricochet: 0.0, ricoBase: 900, debris: null, debrisLevel: 0, duration: 0.45, rustle: true,
  },
};

export const SURFACE_NAMES = Object.keys(SURFACES);

export function surfaceSpec(name) {
  return SURFACES[name] || SURFACES.concrete;
}

/**
 * Schedule a bullet impact for `surface` into `dest`.
 * @returns {{ duration:number }}
 */
export function buildImpact(ac, dest, t0, rng, surface = 'concrete', opts = {}) {
  const S = surfaceSpec(surface);
  const sr = ac.sampleRate;
  const v = (opts.volume ?? 1) * S.level;
  const pitch = j(rng, 0.09);
  let duration = S.duration;

  /* contact transient */
  {
    const d = noiseSlice('white', Math.floor(sr * 0.006), rng);
    applyExpDecay(d, sr, 0.0006);
    highpass(d, sr, Math.max(600, S.hp * 2), 0.7);
    normArr(d, 1);
    emit(ac, dest, t0, d, v * 0.55);
  }

  /* low contact thud */
  if (S.thumpLevel > 0.01) {
    const f0 = S.thumpUp ? S.thumpF : S.thumpF * pitch;
    const f1 = S.thumpUp ? S.thumpF1 * pitch : S.thumpF1 * pitch;
    const d = sineSweep(sr, Math.min(0.4, S.thumpTau * 8), f0, f1, S.thumpTau, rng, 2.2);
    emit(ac, dest, t0, d, v * S.thumpLevel, [bq(ac, 'lowpass', 400, 0.9)]);
  }

  /* material resonance */
  if (S.resLevel > 0.01) {
    const base = S.res * pitch;
    const modes = S.modes.map(([m, a, tau]) => [base * m, a, tau]);
    const d = modal(sr, Math.min(0.95, S.modes[0][2] * 6 + 0.05), modes, rng, 0.05);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.0008, d, v * S.resLevel, [bq(ac, 'highpass', S.hp, 0.7)]);
  }

  /* particulate spray */
  {
    const n = Math.floor(sr * Math.min(0.45, S.noiseTau * 7));
    const d = noiseSlice(S.noiseF < 1200 ? 'brown' : 'white', n, rng);
    bandpass(d, sr, S.noiseF * pitch, S.noiseQ);
    applyAD(d, sr, 0.0015, S.noiseTau);
    normArr(d, 1);
    emit(ac, dest, t0, d, v * S.noiseLevel * 0.6, [bq(ac, 'highpass', S.hp * 0.6, 0.7)]);
  }

  /* water splash: bubbly upward chirps */
  if (S.splash) {
    for (let i = 0; i < 4; i++) {
      const f = 500 + rng() * 1400;
      const d = sineSweep(sr, 0.07, f * 0.55, f, 0.018, rng, 1.5);
      emit(ac, dest, t0 + 0.01 + rng() * 0.14, d, v * 0.16 * (0.4 + rng()), [bq(ac, 'bandpass', f, 2)]);
    }
  }

  /* flesh: short wet squelch */
  if (S.wet) {
    const d = noiseSlice('pink', Math.floor(sr * 0.12), rng);
    bandpass(d, sr, 480 * pitch, 2.4);
    applyAD(d, sr, 0.004, 0.030);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.004, d, v * 0.4);
  }

  /* foliage: a rustle rather than an impact */
  if (S.rustle) {
    const src = noiseSlice('white', Math.floor(sr * 0.03), rng);
    bandpass(src, sr, 4200, 1.2);
    applyExpDecay(src, sr, 0.006);
    const d = granular(sr, 0.35, src, rng, { grains: 26, grainMs: 14, spread: 0.6, pitchJitter: 0.6, gainJitter: 0.8 });
    for (let i = 0; i < d.length; i++) d[i] *= Math.exp(-(i / sr) / 0.10);
    normArr(d, 1);
    emit(ac, dest, t0, d, v * 0.55, [bq(ac, 'highpass', 900, 0.7)]);
  }

  /* ricochet */
  if (S.ricochet > 0 && rng() < S.ricochet * (opts.ricochetScale ?? 1)) {
    const r = buildRicochet(ac, dest, t0 + 0.006 + rng() * 0.01, rng, {
      level: v * 0.5, base: S.ricoBase,
    });
    duration = Math.max(duration, r.duration + 0.05);
  }

  /* debris */
  if (S.debris && S.debrisLevel > 0) {
    const d = buildDebris(ac, dest, t0 + 0.03 + rng() * 0.04, rng, {
      level: v * S.debrisLevel, kind: S.debris, seconds: S.debrisSeconds ?? 0.45,
    });
    duration = Math.max(duration, d.duration + 0.08);
  }

  return { duration };
}

/** A body-armour / metal deflection variant used for penetrated hits. */
export function buildPenetration(ac, dest, t0, rng, opts = {}) {
  const sr = ac.sampleRate;
  const v = opts.volume ?? 0.5;
  const d = noiseSlice('white', Math.floor(sr * 0.09), rng);
  bandpass(d, sr, 2400 * j(rng, 0.2), 1.4);
  applyAD(d, sr, 0.0008, 0.018);
  normArr(d, 1);
  emit(ac, dest, t0, d, v * 0.6, [bq(ac, 'highpass', 800, 0.7)]);
  emit(ac, dest, t0 + 0.001, modal(sr, 0.12, [[1400, 1, 0.03], [2600, 0.5, 0.018]], rng), v * 0.35);
  return { duration: 0.2 };
}

/** Explosion: a huge, saturated, low-dominant blast with a long debris tail. */
export function buildExplosion(ac, dest, t0, rng, opts = {}) {
  const sr = ac.sampleRate;
  const v = (opts.volume ?? 1) * 0.50;
  const power = Math.max(0.2, Math.min(3, opts.power ?? 1));

  // crack
  {
    const d = noiseSlice('white', Math.floor(sr * 0.02), rng);
    applyExpDecay(d, sr, 0.0015);
    highpass(d, sr, 1200, 0.7);
    normArr(d, 1);
    emit(ac, dest, t0, d, v * 0.55);
  }
  // body: brown noise through a slow down-sweep with heavy drive
  {
    const n = Math.floor(sr * 1.4);
    const d = noiseSlice('brown', n, rng);
    applyAD(d, sr, 0.004, 0.34 * power);
    normArr(d, 1);
    const f = bq(ac, 'lowpass', 2600, 3.2);
    f.frequency.setValueAtTime(2600, t0);
    f.frequency.exponentialRampToValueAtTime(120, t0 + 0.45 * power);
    emit(ac, dest, t0, d, v * 0.95, [f]);
  }
  // sub thump
  {
    const d = sineSweep(sr, 1.1, 92 / power, 28 / power, 0.30 * power, rng, 2);
    emit(ac, dest, t0 + 0.002, d, v * 1.0, [bq(ac, 'lowpass', 160, 0.9)]);
  }
  // shrapnel + debris rain
  {
    const src = noiseSlice('white', Math.floor(sr * 0.04), rng);
    bandpass(src, sr, 2600, 1.4);
    applyExpDecay(src, sr, 0.008);
    const d = granular(sr, 1.6, src, rng, { grains: 60, grainMs: 16, spread: 0.4, pitchJitter: 0.7, gainJitter: 0.9 });
    for (let i = 0; i < d.length; i++) d[i] *= Math.exp(-(i / sr) / 0.5);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.06, d, v * 0.3, [bq(ac, 'highpass', 500, 0.7)]);
  }
  // rolling low tail
  {
    const n = Math.floor(sr * 2.0);
    const d = noiseSlice('brown', n, rng);
    lowpass(d, sr, 220, 0.8);
    applyAD(d, sr, 0.05, 0.55 * power);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.03, d, v * 0.45);
  }
  return { duration: 2.4 };
}
